import { Inject, Injectable, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { APP_CONFIG, AppConfig, AppHostService, PersistentStore } from "app/core";
import {
  BehaviorSubject,
  Observable,
  Subject,
  delay,
  filter,
  map,
  switchMap,
  take,
  tap,
} from "rxjs";
import { CHAT_CLOSING_DELAY, CHAT_OPEN_PERSISTENT_STORE } from "./di-tokens";

/** Enables to store the chat opening status */
export interface PersistedChatOpenState {
  open: string;
}

/**
 * @return Whether the application should open the chatbot by default, according to the
 * "whereToBeOpenByDefault" configuration key
 */
function shouldOpenByDefault(config: AppConfig, hostService: AppHostService): boolean {
  return !!(
    (config.whereToBeOpenByDefault &&
      hostService.currentUrl.includes(config.whereToBeOpenByDefault)) ||
    hostService.currentUrl.includes("ui.chatbotrh")
  );
}

/**
 * A store for the chat open state.
 * When the chat is open, it is active, otherwise, no message can be
 * emitted or received (once the current polling sequence is over).
 *
 * The chat should be opened at startup if:
 * - The configuration forces the opening at all times (config.open = true)
 * - The configuration forces the opening for the current URL (whereToBeOpenByDefault)
 * - The bot was opened since less than one hour in the current tab or in another one (as defined in local storage)
 *
 * If the bot is opened at startup, the local storage is not updated: if the opening should be
 * forced for the current URL, the other tabs with different URLs should not be impacted.
 *
 */
@Injectable({ providedIn: "root" })
export class ChatOpenStore {
  private value$: BehaviorSubject<boolean>;
  private delayedValue$ = new Subject<boolean>();

  public isOpen: Signal<boolean>;

  constructor(
    @Inject(APP_CONFIG) config: AppConfig,
    hostService: AppHostService,
    @Inject(CHAT_OPEN_PERSISTENT_STORE)
    private persistentStore: PersistentStore<PersistedChatOpenState>,
    @Inject(CHAT_CLOSING_DELAY)
    chatClosingDelay: number
  ) {
    this.value$ = new BehaviorSubject<boolean>(shouldOpenByDefault(config, hostService));
    this.isOpen = toSignal(this.value$.asObservable(), { initialValue: false });

    hostService.channelReady$
      .pipe(
        take(1),
        switchMap(() => this.persistentStore.get()),
        // Get value from local storage : can be synchronous or asynchronous when sandbox iframe mode is activated.
        map((state) => shouldOpenByDefault(config, hostService) || state?.open === "true"),
        tap((value) => {
          // Preserve an explicit open() call that happened while the async GET was in flight
          // (e.g. stage="open" fires before the localStorage round-trip completes).
          this.value$.next(value || this.value$.getValue());
        })
      )
      .subscribe();

    // listen to close events in delayedValue$, delay those events, and then close
    this.delayedValue$
      .pipe(
        filter((isOpening: boolean) => !isOpening),
        delay(chatClosingDelay)
      )
      .subscribe(() => this.close());
  }

  /**
   * @return An observable to the events of the opening and closing of the chatbot.
   * The event contains the open state: a boolean stating if the chatbot is open or not
   */
  get isOpen$(): Observable<boolean> {
    return this.value$.asObservable();
  }

  /** @return An observable to the events of the opening of the chatbot. The event contains no value. */
  get isOpening$(): Observable<void> {
    return this.value$.pipe(
      filter((val: boolean) => val === true),
      map(() => {
        return;
      })
    );
  }

  /** @return An observable to the events of the closing of the chatbot. The event contains no value. */
  get isClosing$(): Observable<void> {
    return this.value$.pipe(
      filter((val: boolean) => val === false),
      map(() => {
        return;
      })
    );
  }

  private setOpen(newValue: boolean) {
    this.persistentStore.set({ open: newValue + "" });
    this.value$.next(newValue);
  }

  /** Toggles the opening state of the chatbot. The new state is persisted. */
  open(): void {
    this.setOpen(true);
  }

  /** Closes the chatbot. The new state is persisted. */
  close(): void {
    this.setOpen(false);
  }

  /** Closes the chatbot, with a delay. The new state is persisted. */
  closeWithDelay(): void {
    this.delayedValue$.next(false);
  }
}
